import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import IntelTable from './components/IntelTable'
import Badge from './components/Badge'
import { formatNumber } from './components/intelTheme'
import { intelApi } from './api'
import { ChartCard, TrendLine, Donut, StackedBars } from './components/IntelCharts'

const pretty = (c) => (c ? c.replace(/_/g, ' ') : c)

// For stuck orders, MORE is BAD → ▲ up = red, ▼ down = green (inverted vs a normal KPI).
const stuckDelta = (v) => {
  if (v === null || v === undefined || isNaN(v)) return { text: '—', color: '#888' }
  const n = Number(v)
  if (n > 0) return { text: `▲ +${n.toFixed(0)}%`, color: '#e53935' }
  if (n < 0) return { text: `▼ ${n.toFixed(0)}%`, color: '#43a047' }
  return { text: '— flat', color: '#888' }
}

// Failure stage → badge colour (mirrors the mockup's coloured category chips).
const stageColor = { ALLOCATION: 'orange', COURIER: 'red', CONFIG: 'grey', FINANCE: 'blue', DATA: 'grey', OTHER: 'grey' }
const STAGE_TINT = { ALLOCATION: '#fff8f0', COURIER: '#fff5f7' }

export default function StuckTrends() {
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .stuckTrends({ days: 90 })
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [])

  const points = useMemo(() => data?.points || [], [data])
  const weeklyAsc = useMemo(() => data?.weekly || [], [data])
  const kpis = data?.kpis
  const breakdownDate = data?.breakdownDate

  // Donut = failure mix on the breakdown day (matches the mockup's single-day "Category Mix").
  const donutData = useMemo(
    () => (data?.categoryBreakdown || []).map((r) => ({ name: r.label, value: r.ordersToday })),
    [data]
  )

  // Category breakdown rows + a TOTAL footer row.
  const breakdownRows = useMemo(() => {
    const rows = (data?.categoryBreakdown || []).map((r) => ({ ...r }))
    if (rows.length) {
      const totalOrders = rows.reduce((s, r) => s + (r.ordersToday || 0), 0)
      rows.push({ __total: true, label: 'TOTAL', ordersToday: totalOrders })
    }
    return rows
  }, [data])

  // Monthly inflow by category (stacked) — built from daily points.
  const monthly = useMemo(() => {
    const byMonth = {}
    const catTotals = {}
    points.forEach((p) => {
      const m = (p.date || '').slice(0, 7)
      if (!m) return
      byMonth[m] = byMonth[m] || {}
      Object.entries(p.categoryCounts || {}).forEach(([k, v]) => {
        byMonth[m][k] = (byMonth[m][k] || 0) + v
        catTotals[k] = (catTotals[k] || 0) + v
      })
    })
    const topKeys = Object.entries(catTotals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k]) => pretty(k))
    const rows = Object.keys(byMonth).sort().map((m) => {
      const row = { month: m }
      Object.entries(byMonth[m]).forEach(([k, v]) => { if (topKeys.includes(pretty(k))) row[pretty(k)] = v })
      topKeys.forEach((k) => { if (row[k] == null) row[k] = 0 })
      return row
    })
    return { rows, keys: topKeys }
  }, [points])

  const d7 = stuckDelta(kpis?.wow7dPct)
  const d30 = stuckDelta(kpis?.wow30dPct)

  const catCols = [
    { key: 'cat', header: 'Failure Category', render: (r) => (r.__total ? <strong>TOTAL</strong> : <Badge color={stageColor[r.stage] || 'grey'}>{r.label}</Badge>) },
    { key: 'root', header: 'Root Cause', render: (r) => (r.__total ? '' : <span style={{ color: '#555' }}>{r.rootCause || '—'}</span>) },
    { key: 'cnt', header: 'Orders', render: (r) => <strong>{formatNumber(r.ordersToday)}</strong> },
    { key: 'pct', header: '% of Total', render: (r) => (r.__total ? '' : r.pctToday != null ? `${r.pctToday}%` : '—') },
    {
      key: 'wow',
      header: 'WoW Trend',
      info: 'Last 7 days vs the prior 7 days for this category. ▲ (red) = inflow worsening, ▼ (green) = improving.',
      render: (r) => {
        if (r.__total) return ''
        const d = stuckDelta(r.wowPct)
        return <span style={{ color: d.color, fontWeight: 600 }}>{d.text}</span>
      },
    },
    {
      key: 'cp',
      header: 'Top Courier(s) Affected',
      info: 'Couriers attempted (from cpErrorList) on the breakdown day. Allocation-stage failures are courier-agnostic ("All couriers").',
      render: (r) => (r.__total ? '' : <span style={{ fontSize: 11 }}>{r.topCouriers || '—'}</span>),
    },
    { key: 'res', header: 'Resolution', render: (r) => (r.__total ? '' : <span style={{ fontSize: 11, color: '#555' }}>{r.resolution || '—'}</span>) },
  ]

  return (
    <IntelLayout title='Stuck Orders — Trends'>
      <Box sx={{ background: '#e8f4fd', border: '1px solid #b3d9f2', borderRadius: '6px', p: '10px 14px', mb: 2, fontSize: 12, color: '#1565c0' }}>
        Inflow grouped by order <code>createdDate</code> — live <code>orders</code> + 180-day{' '}
        <code>unprocessed_orders_archive</code>.
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='red' label='Stuck Today' value={formatNumber(kpis?.todayTotal)} sub='Created today' />
        <KpiCard loading={loading} color='orange' label='7-Day Avg / day' value={kpis?.avg7d != null ? formatNumber(kpis.avg7d) : '—'} sub={`${d7.text} vs prior week`} subColor={d7.color} />
        <KpiCard loading={loading} color='purple' label='30-Day Avg / day' value={kpis?.avg30d != null ? formatNumber(kpis.avg30d) : '—'} sub={`${d30.text} vs 30d ago`} subColor={d30.color} />
        <KpiCard loading={loading}
          color='blue'
          label='Top Failure'
          valueSize={14}
          value={kpis?.topCategoryLabel || '—'}
          sub={kpis?.topCategoryCount ? `${formatNumber(kpis.topCategoryCount)} (${kpis.topCategoryPct != null ? kpis.topCategoryPct : 0}%)` : '—'}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', mb: 2 }}>
        <ChartCard loading={loading} title='Weekly Unprocessed Inflow — last ~13 weeks'>
          <TrendLine data={weeklyAsc} yKey='total' name='Stuck' color='#1565c0' area height={260} />
        </ChartCard>
        <ChartCard loading={loading} title={`Failure Category Mix${breakdownDate ? ` (${breakdownDate})` : ''}`}>
          <Donut data={donutData} height={260} />
        </ChartCard>
      </Box>

      <IntelTable
        title={`Failure Category Breakdown${breakdownDate ? ` — ${breakdownDate}` : ''}`}
        loading={loading}
        columns={catCols}
        rows={breakdownRows}
        rowKey={(r, i) => (r.__total ? 'total' : r.code || i)}
        rowStyle={(r) => (r.__total ? { background: '#fafafa', fontWeight: 700 } : STAGE_TINT[r.stage] ? { background: STAGE_TINT[r.stage] } : undefined)}
        emptyMsg='No stuck orders on the latest day in the window'
      />

      <Box sx={{ mb: 2 }}>
        <ChartCard loading={loading} title='Monthly Inflow by Failure Category'>
          <StackedBars data={monthly.rows} xKey='month' keys={monthly.keys} height={300} />
        </ChartCard>
      </Box>
    </IntelLayout>
  )
}
